/**
 * Utility for image processing and client-side conversion to WebP format.
 */

/**
 * Converts any image File or Blob into WebP format using HTML5 Canvas.
 * Automatically resizes image if it exceeds maxWidth or maxHeight while preserving aspect ratio.
 * 
 * @param {File|Blob} fileOrBlob - Input image file or blob
 * @param {Object} options
 * @param {number} [options.maxWidth=1920] - Maximum width constraint
 * @param {number} [options.maxHeight=1920] - Maximum height constraint
 * @param {number} [options.quality=0.85] - WebP quality (0.0 to 1.0)
 * @param {string} [options.fallbackName='accesorio.webp'] - Name to use for the returned File
 * @returns {Promise<{ file: File, previewUrl: string, originalSize: number, webpSize: number, width: number, height: number }>}
 */
export async function convertToWebP(fileOrBlob, options = {}) {
    const {
        maxWidth = 1920,
        maxHeight = 1920,
        quality = 0.85,
        fallbackName = 'accesorio.webp'
    } = options;

    const originalSize = fileOrBlob.size || 0;

    return new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(fileOrBlob);

        img.onload = () => {
            URL.revokeObjectURL(objectUrl);

            let { naturalWidth: width, naturalHeight: height } = img;
            if (!width || !height) {
                width = img.width || 800;
                height = img.height || 600;
            }

            // Downscale proportionally if larger than maximum limits
            if (width > maxWidth || height > maxHeight) {
                const ratio = Math.min(maxWidth / width, maxHeight / height);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Canvas 2D context not supported'));
                return;
            }

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        reject(new Error('No se pudo convertir la imagen a formato WebP'));
                        return;
                    }

                    // Form a clean filename with .webp extension
                    let baseName = 'accesorio';
                    if (fileOrBlob.name) {
                        baseName = fileOrBlob.name.replace(/\.[^/.]+$/, '');
                    } else if (fallbackName) {
                        baseName = fallbackName.replace(/\.[^/.]+$/, '');
                    }
                    const cleanBaseName = baseName.replace(/[^a-zA-Z0-9_-]/g, '_');
                    const webpFileName = `${cleanBaseName}_${Date.now()}.webp`;

                    const webpFile = new File([blob], webpFileName, {
                        type: 'image/webp',
                        lastModified: Date.now()
                    });

                    const previewUrl = URL.createObjectURL(blob);

                    resolve({
                        file: webpFile,
                        previewUrl,
                        originalSize,
                        webpSize: blob.size,
                        width,
                        height
                    });
                },
                'image/webp',
                quality
            );
        };

        img.onerror = (err) => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Error al cargar la imagen para procesarla: ' + (err?.message || 'Formato no soportado')));
        };

        img.src = objectUrl;
    });
}

/**
 * Formats byte size to human readable string (KB / MB).
 */
export function formatBytes(bytes, decimals = 1) {
    if (!bytes || bytes <= 0) return '0 B';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}
